import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { DollarSign, Receipt, TrendingDown, PiggyBank, CreditCard, ArrowRight } from "lucide-react";
import SummaryCard from "../components/SummaryCard";
import ProgressBar from "../components/ProgressBar";
import { getPaychecks, getBills, getSavings, getDebts } from "../utils/api";
import {
  calcMonthlyIncome, calcMonthlyBills, calcTotalSavings, calcTotalDebt,
  calcLeftover, fmt,
} from "../utils/calculations";
import { useTheme } from "../contexts/ThemeContext";

const CATEGORY_COLORS = {
  Housing: "#7c3aed", Utilities: "#f59e0b", Subscriptions: "#ec4899",
  Transportation: "#f97316", Groceries: "#10b981", Insurance: "#14b8a6",
  Entertainment: "#6366f1", Medical: "#ef4444", Other: "#6b7280",
};

export default function Dashboard() {
  const { dark } = useTheme();
  const [paychecks, setPaychecks] = useState([]);
  const [bills,     setBills]     = useState([]);
  const [goals,     setGoals]     = useState([]);
  const [debts,     setDebts]     = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([getPaychecks(), getBills(), getSavings(), getDebts()])
      .then(([p, b, g, d]) => { setPaychecks(p); setBills(b); setGoals(g); setDebts(d); })
      .finally(() => setLoading(false));
  }, []);

  const monthlyIncome = calcMonthlyIncome(paychecks);
  const monthlyBills  = calcMonthlyBills(bills);
  const leftover      = calcLeftover(paychecks, bills);
  const totalSavings  = calcTotalSavings(goals);
  const totalDebt     = calcTotalDebt(debts);

  const suggestSave = leftover > 0 && goals.length > 0 ? leftover * 0.6 : leftover > 0 ? leftover : 0;
  const suggestDebt = leftover > 0 && debts.length > 0 ? leftover * 0.4 : 0;

  const barData = [
    { name: "Income",   value: monthlyIncome },
    { name: "Expenses", value: monthlyBills },
  ];

  const categoryMap = {};
  bills.forEach((b) => {
    const cat = b.category || "Other";
    const monthly = b.frequency === "monthly" ? b.amount
      : b.frequency === "annual"     ? b.amount / 12
      : b.frequency === "weekly"     ? b.amount * 52 / 12
      : b.frequency === "bi-weekly"  ? b.amount * 26 / 12
      : b.amount;
    categoryMap[cat] = (categoryMap[cat] || 0) + monthly;
  });
  const pieData = Object.entries(categoryMap).map(([name, value]) => ({ name, value: Math.round(value) }));

  // Chart theme colours
  const gridColor   = dark ? "#374151" : "#f0f0f0";
  const tickColor   = dark ? "#9ca3af" : "#6b7280";
  const tooltipBg   = dark ? "#1f2937" : "#fff";
  const tooltipBorder = dark ? "#374151" : "#e5e7eb";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const hasData = paychecks.length > 0 || bills.length > 0;

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Your household financial snapshot</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard title="Monthly Income"    value={fmt(monthlyIncome)} subtitle={`${paychecks.length} paycheck${paychecks.length !== 1 ? "s" : ""}`} icon={DollarSign} color="blue" />
        <SummaryCard title="Monthly Bills"     value={fmt(monthlyBills)}  subtitle={`${bills.length} bill${bills.length !== 1 ? "s" : ""}`} icon={Receipt} color="yellow" />
        <SummaryCard title="Remaining Balance" value={fmt(leftover)}       subtitle="After all bills" icon={TrendingDown} color={leftover >= 0 ? "green" : "red"} />
        <SummaryCard title="Total Savings"     value={fmt(totalSavings)}  subtitle={`${goals.length} goal${goals.length !== 1 ? "s" : ""}`} icon={PiggyBank} color="green" />
        <SummaryCard title="Total Debt"        value={fmt(totalDebt)}     subtitle={`${debts.length} account${debts.length !== 1 ? "s" : ""}`} icon={CreditCard} color="red" />
      </div>

      {!hasData && (
        <div className="card text-center py-12">
          <TrendingDown className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No data yet</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Add your paychecks and bills to see your financial overview.</p>
          <a href="/paychecks" className="btn-primary inline-flex">Get Started <ArrowRight className="w-4 h-4" /></a>
        </div>
      )}

      {hasData && (
        <>
          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Monthly Income vs. Expenses</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: tickColor }} />
                  <YAxis tick={{ fontSize: 11, fill: tickColor }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v) => fmt(v)}
                    contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: "8px", color: dark ? "#f3f4f6" : "#111827" }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} name="Amount">
                    <Cell fill="#2563eb" />
                    <Cell fill="#ef4444" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Spending by Category</h3>
              {pieData.length === 0 ? (
                <div className="flex items-center justify-center h-[220px] text-gray-400 dark:text-gray-500 text-sm">No bills added yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false} fontSize={11}>
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={CATEGORY_COLORS[entry.name] || "#6b7280"} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v) => fmt(v)}
                      contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: "8px", color: dark ? "#f3f4f6" : "#111827" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Leftover money indicator */}
          {leftover > 0 && (
            <div className="card bg-gradient-to-r from-secondary-50 to-primary-50 dark:from-secondary-900/20 dark:to-primary-900/20 border-secondary-200 dark:border-secondary-800/50">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Leftover Money — {fmt(leftover)}/mo available
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Suggested allocation after all bills are paid:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {goals.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-secondary-100 dark:border-secondary-800/50">
                    <div className="flex items-center gap-2 mb-1">
                      <PiggyBank className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Save</span>
                    </div>
                    <p className="text-xl font-bold text-secondary-600 dark:text-secondary-400">{fmt(suggestSave)}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">60% toward savings goals</p>
                  </div>
                )}
                {debts.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-red-100 dark:border-red-800/50">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Pay Down Debt</span>
                    </div>
                    <p className="text-xl font-bold text-red-600 dark:text-red-400">{fmt(suggestDebt)}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">40% toward debt payoff</p>
                  </div>
                )}
                {goals.length === 0 && debts.length === 0 && (
                  <p className="col-span-2 text-sm text-gray-500 dark:text-gray-400">Add savings goals or debts to see a suggested split.</p>
                )}
              </div>
            </div>
          )}

          {leftover < 0 && (
            <div className="card bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-800 dark:text-red-300">Spending exceeds income</h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-0.5">
                    Your bills exceed your income by {fmt(Math.abs(leftover))}/mo. Consider reviewing your expenses in the Bills section.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Savings goals progress */}
          {goals.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Savings Goals Progress</h3>
              <div className="space-y-5">
                {goals.slice(0, 4).map((g) => (
                  <div key={g.id}>
                    <div className="flex justify-between items-baseline mb-1.5">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{g.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{fmt(g.currentAmount || 0)} / {fmt(g.targetAmount)}</span>
                    </div>
                    <ProgressBar value={g.currentAmount || 0} max={g.targetAmount} color="green" showLabel={false} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Debt progress */}
          {debts.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Debt Payoff Progress</h3>
              <div className="space-y-5">
                {debts.slice(0, 4).map((d) => (
                  <div key={d.id}>
                    <div className="flex justify-between items-baseline mb-1.5">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{d.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{fmt(d.balance)} remaining of {fmt(d.originalBalance || d.balance)}</span>
                    </div>
                    <ProgressBar value={(d.originalBalance || d.balance) - d.balance} max={d.originalBalance || d.balance} color="red" showLabel={false} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
